import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  Batch,
  BatchMailing,
  BatchMailingType,
  BatchMailingData,
  BatchProperties,
} from '../../models';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { adaptObject, BATCH_MAILING_DATA } from '../../../batches';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import {
  catchError,
  debounceTime,
  finalize,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { focusFirstInvalidField, triggerFormValidation } from '$core/helpers';
import {
  BatchesParseStorageService,
  BatchesService,
  BatchMailingCreateParams,
} from '../../services';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BatchMailingFormComponent } from '../../components';
import { ECOMMERCE_POSTAL_DELIVERY_TYPE } from '../../contants';

@Component({
  selector: 'app-add-batch-mailing',
  templateUrl: './add-batch-mailing.component.html',
  styleUrls: ['./add-batch-mailing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddBatchMailingComponent implements OnInit, OnDestroy {
  @ViewChild(BatchMailingFormComponent, { static: false })
  public formComponent: BatchMailingFormComponent;

  @ViewChild(BatchMailingFormComponent, { static: false, read: ElementRef })
  public formComponentElement: ElementRef;

  public readonly isFullScreen$ = this.batchesParseStorageService.isFullScreen$;
  public readonly isInitialLoading$ = new BehaviorSubject<boolean>(false);
  public readonly isBatchMailingProcessing$ = new BehaviorSubject<boolean>(
    false
  );
  public readonly batchProperties$ =
    new BehaviorSubject<BatchProperties | null>(null);
  public readonly batchMailingData$ = new BehaviorSubject<
    BatchMailingData[BatchMailingType] | null
  >(null);
  public readonly batch$ = new BehaviorSubject<Batch | null>(null);

  public readonly batchId = this.activatedRoute.snapshot.params['id'];

  public readonly duplicateControl = new FormControl(false);
  public readonly duplicatesCountControl = new FormControl(1);
  public readonly duplicatesCountOptions = [1, 2, 3, 4, 5];

  private readonly BATCH_MAILING_DATA = BATCH_MAILING_DATA;

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly batchesService: BatchesService,
    private readonly batchesParseStorageService: BatchesParseStorageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly renderer: Renderer2
  ) {}

  @HostListener('document:keydown.escape')
  public onEscapeKeyPress(): void {
    if (this.isFullScreen$.getValue()) {
      this.toggleFullScreen();
    }
  }

  public ngOnInit(): void {
    this.loadData();
  }

  public ngOnDestroy(): void {}

  public back(): void {
    this.router.navigate([`personal/batch/batches/${this.batchId}/view-batch`]);
  }

  public submit(isAddNext: boolean = false): void {
    if (this.form.invalid) {
      triggerFormValidation(this.form);
      focusFirstInvalidField(this.formComponentElement);

      if (
        this.batchProperties.isOrderedLetterOrPostcard &&
        this.form.get('hand_over_personally').value === true &&
        this.form.get('hand_over_personally').invalid
      ) {
        this.form.get('hand_over_personally').patchValue(null);
      }

      return;
    }

    this.createBatchMailing(this.form, isAddNext);
  }

  public navigateToNextPage(resp: BatchMailing): void {
    switch (this.batchProperties.isPartialReceipt) {
      case true:
        this.router.navigate(
          [
            `personal/batch/batches/${this.batch.id}/created/view-departure-attachments`,
          ],
          {
            queryParams: {
              departureId: resp.id,
            },
          }
        );

        break;

      case false:
        this.router.navigate([
          `personal/batch/batches/${this.batch.id}/view-batch`,
        ]);

        break;
    }
  }

  public toggleFullScreen(): void {
    this.batchesParseStorageService.toggleFullScreen(this.renderer);
  }

  public get batchProperties(): BatchProperties | null {
    return this.batchProperties$.value;
  }

  private loadData(): void {
    this.isInitialLoading$.next(true);

    this.batchesService
      .show({ batchId: this.batchId })
      .pipe(
        finalize(() => this.isInitialLoading$.next(false)),
        untilDestroyed(this)
      )
      .subscribe((batch) => {
        this.batch$.next(batch);

        const properties = this.setupBatchProperties(batch);

        this.batchProperties$.next(properties);

        this.batchMailingData$.next(
          this.BATCH_MAILING_DATA[batch.postal_delivery_type]
        );

        setTimeout(() => this.observeForm(), 1000);
      });
  }

  private observeForm(): void {
    this.formComponent.form$
      .pipe(
        switchMap((form) =>
          form.valueChanges.pipe(
            debounceTime(500),
            startWith(form.value),
            untilDestroyed(this)
          )
        )
      )
      .subscribe(() => {
        if (this.form.valid) {
          this.calculate();
        }
      });
  }

  private calculate(): void {
    const body = adaptObject(this.form.getRawValue());

    this.isBatchMailingProcessing$.next(true);

    this.batchesService
      .calculate(this.batch.id, body)
      .pipe(
        catchError((errors) => {
          if (errors.error.errors) {
            this.parseErrors(errors.error.errors);
          }

          this.cd.detectChanges();

          return EMPTY;
        }),
        finalize(() => this.isBatchMailingProcessing$.next(false)),
        untilDestroyed(this)
      )
      .subscribe((response) => {
        this.form.get('cost').patchValue(response.cost, { emitEvent: false });
        this.formComponent.markForCheck();
      });
  }

  private parseErrors(errors: Params): void {
    Object.keys(errors).forEach((key) => {
      const error = errors[key];
      if (key.includes('addons.')) {
        key = key.replace('addons.', '');
      }

      const [items, controlIndex, controlName] = key.split('.');
      if (items && items.startsWith('items')) {
        const formGroup = this.form;
        const control = formGroup.get(controlName);
        if (
          control &&
          control.statusChanges &&
          formGroup &&
          formGroup.statusChanges
        ) {
          control.setErrors(error, { emitEvent: false });
          control.markAsTouched();
          formGroup.markAsDirty();
        } else {
          console.error(`Control with name ${controlName} not found`);
        }
      } else {
        const formGroup = this.form;

        switch (key) {
          case 'recipient_contact_widget_data': {
            const control = formGroup.get('recipient');

            const recipientErrors = error.map((item) => {
              switch (item) {
                case 'validation.recipient_contact_widget_data_required':
                  return 'Обязательное поле';
                default:
                  return item;
              }
            });

            if (!control) return;

            control.setErrors(recipientErrors, { emitEvent: false });
            control.markAsTouched({ onlySelf: true });

            formGroup.markAsDirty();

            break;
          }

          default: {
            const control = formGroup.get(key);

            if (!control) return;

            control.setErrors(error, { emitEvent: false });
            control.markAsTouched({ onlySelf: true });
            formGroup.markAsDirty();

            break;
          }
        }
      }
    });

    this.cd.detectChanges();
    this.formComponent.markForCheck();
  }

  private createBatchMailing(form: FormGroup, isAddNext: boolean) {
    const params: BatchMailingCreateParams = {
      batchId: this.batch.id,
      ...adaptObject(form.getRawValue()),
      duplicates_count: this.duplicateControl.value
        ? this.duplicatesCountControl.value
        : null,
    };

    this.isBatchMailingProcessing$.next(true);
    this.batchesService
      .createMailing(params)
      .pipe(
        catchError((errors) => {
          if (errors.error.errors) {
            this.parseErrors(errors.error.errors);
          }

          return EMPTY;
        }),
        finalize(() => this.isBatchMailingProcessing$.next(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (resp: BatchMailing) => {
          if (isAddNext) {
            this.form.reset();

            this.formComponent.setupForm();

            return;
          } else {
            return this.navigateToNextPage(resp);
          }
        },
      });
  }

  private setupBatchProperties(batch: Batch): BatchProperties {
    return {
      isEcommerce: ECOMMERCE_POSTAL_DELIVERY_TYPE.has(
        batch.postal_delivery_type
      ),
      isELS: batch.payment_type === 'electronic_personal_account',
      isCommitmentLetter: batch.payment_type === 'commitment_letter',
      isEMS: batch.postal_delivery_type === 'ems',
      isPartialReceipt: batch.is_partial_receipt,
      isOrderedLetterOrPostcard:
        batch.postal_delivery_type === 'ordered_letter' ||
        batch.postal_delivery_type === 'ordered_postcard',
    };
  }

  private get batch(): Batch | null {
    return this.batch$.value;
  }

  private get form(): FormGroup | null {
    if (!this.formComponent) {
      return null;
    }

    return this.formComponent.form;
  }
}
